import React, { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import checkStatus from './jsFunctions/checkStatus.js'

import scriptjs from 'scriptjs';
import URL2 from './config.js';

import { Box, Card, Typography, Button, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

import Heading from './components/Heading/Heading.js';
import MainTemplate from './components/MainTemplate/MainTemplate';
import LoadingBackdrop from './components/LoadingBackdrop/LoadingBackdrop.js';

import ParentDetails from './components/ParentDetails/ParentDetails.js';
import {
    clearCampusPaymentCharges,
    clearPaymentResumeState,
    storeCampusPaymentCharges,
    storeCampusSessions,
    storePaymentResumeState,
} from './utils/campusPaymentCharges.js';
import { shouldRedirectToCompletion } from './utils/campusPaymentStatus.js';
import { countFeeEligibleChildren } from './utils/parentHistoryHelpers.js';
import { cardPaymentSteps, stepFor } from './utils/campusPaymentSteps.js';
import CampusPaymentSteps from './components/CampusPaymentSteps/CampusPaymentSteps.jsx';

/**
 * BPoint form — shows the family card total. Each unpaid campus is charged
 * separately on the backend (one AuthKey / merchant per campus).
 */
/**
 * Whether a refusal to start a card payment means the family has paid everything: a 400 whose
 * calculation shows every campus paid. getauthkey-v2 answers 400 when there is no unpaid campus left
 * to charge, which is what stops a family paying a campus twice.
 */
export const everyCampusPaid = (status, calculation) => {
    if (status !== 400 || !calculation) return false;
    if (calculation.payment?.allCampusesPaid === true) return true;
    const rows = calculation.campusBreakdown || calculation.payment?.campusBreakdown || [];
    return rows.length > 0 && rows.every((row) => row.paymentStatus === 'paid');
};

const BpointPaymentForm = () => {
    const [authkey, setauthkey] = useState();
    const [chargeAmount, setChargeAmount] = useState();
    const [campusChargeAmount, setCampusChargeAmount] = useState();
    const [displayTotals, setDisplayTotals] = useState({});
    const [campusBreakdown, setCampusBreakdown] = useState([]);
    const [activeCharge, setActiveCharge] = useState(null);
    const [initError, setInitError] = useState('');
    const [isInitializing, setIsInitializing] = useState(true);
    const [formReady, setFormReady] = useState(false);
    // The server found nothing left to charge, and its breakdown shows every campus paid.
    const [allPaid, setAllPaid] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDemo = String(process.env.REACT_APP_DEMO).toLowerCase() === 'true';
    const domainUrl = useRef();
    const styleTimeout = useRef();

    const state = location.state || {};
    const SessionID = state.SessionID;
    const parentID = state.parentID;
    const college = state.college;
    const famName = state.famName != null ? String(state.famName) : '';
    const childrens = state.childrens || state.enrolledchildrens;
    const SelectedDiscountValue = state.SelectedDiscountValue || state.DiscountOption;
    const RemAmountMethod = state.RemAmountMethod;
    const FTE = state.FTE;
    const SD = state.SD;
    const credit_amount = state.credit_amount;
    const customAmount = state.customAmount ?? state.CustomAmount;
    const enrolledchildrens = state.enrolledchildrens || state.childrens;

    const TotalResAmount = displayTotals.TotalResAmount ?? state.TotalResAmount;
    const TotalDepFees = displayTotals.TotalDepFees ?? state.TotalDepFees;
    const PayableAmount = displayTotals.PayableAmount ?? state.PayableAmount;
    const CustomAmount = displayTotals.CustomAmount ?? state.CustomAmount;
    const AmountPaidFromBpoint = chargeAmount ?? state.AmountPaidFromBpoint;
    const ChargeCampusAmount = campusChargeAmount
        ?? activeCharge?.AmountPaidFromBpoint
        ?? AmountPaidFromBpoint;
    const paidCampuses = campusBreakdown.filter((row) => row.paymentStatus === 'paid');
    const failedCampuses = campusBreakdown.filter((row) => row.paymentStatus === 'failed');
    const cardCampusRows = campusBreakdown.filter(
        (row) => Number(row.AmountPaidFromBpoint) > 0 && row.paymentStatus !== 'paid'
    );
    const money = (value) => `$${Number(value || 0).toFixed(2)}`;

    // One card payment per campus. With more than one, the parent is told the shape of it before the
    // card form appears, and which payment this is while it is on screen.
    const steps = cardPaymentSteps(campusBreakdown);
    const isMultiStep = steps.length > 1;
    const current = stepFor(steps, activeCharge?.SessionID);
    const [started, setStarted] = useState(false);

    /**
     * Whether the card form may be revealed.
     *
     * Two things used to make this true when it should not have been, and both put a parent on the
     * card fields without ever seeing how many payments there are.
     *
     * `steps.some((step) => step.paid)` — a family with one campus already paid skipped the intro
     * altogether. That is backwards: a parent returning to pay Thornlie after Kewdale went through
     * is exactly the person who needs telling that this is payment 2 of 2 and what it is for. The
     * intro handles them properly, listing Kewdale as Paid and offering Start for the campus left.
     *
     * `!isMultiStep` before the server has answered — campusBreakdown starts empty, so steps is
     * empty and isMultiStep is false, which read as "there is only one payment to make". Waiting
     * for isInitializing rather than for a non-empty breakdown keeps a family whose breakdown comes
     * back empty able to pay: after init, one step or none still goes straight to the card.
     */
    const readyToPay = !isInitializing && (!isMultiStep || started);

    useEffect(() => {
        const init = async () => {
            try {
                if (!localStorage.getItem('LoginToken')) {
                    navigate('/');
                    return;
                }

                const data = await checkStatus({ campusSessions: state.campusSessions || [] });
                if (!data || data.RenrolStatus === undefined) return;

                if (shouldRedirectToCompletion(data)) {
                    navigate('/reenrolmentcompleted', {
                        state: { t_date: data.t_date, t_time: data.t_time, campusStatuses: data.campusStatuses || [] },
                    });
                    return;
                }

                const domain = window.location.port
                    ? `${window.location.protocol}//${window.location.hostname}:${window.location.port}`
                    : `${window.location.protocol}//${window.location.hostname}`;

                domainUrl.current = domain;

                const { data: authData } = await URL2.post('/getauthkey-v2', {
                    SessionID,
                    parentID,
                    college,
                    famName: encodeURIComponent(famName),
                    domainUrl: domain,
                    DiscountOption: SelectedDiscountValue,
                    SelectedDiscountValue,
                    SD,
                    FTE,
                    credit_amount,
                    customAmount,
                    enrolledchildrens,
                    NoOfChildren: countFeeEligibleChildren(enrolledchildrens),
                    campusSessions: state.campusSessions || [],
                    campusPaymentSelections: state.campusPaymentSelections || [],
                    earlyBirdAvailable: state.earlyBirdAvailable,
                    GiveHCCDiscount: state.GiveHCCDiscount,
                });

                setChargeAmount(authData.AmountPaidFromBpoint);
                setCampusChargeAmount(
                    authData.ChargeCampusAmount
                    ?? authData.chargeCampus?.AmountPaidFromBpoint
                    ?? authData.AmountPaidFromBpoint
                );
                const breakdown = authData.calculation?.campusBreakdown || [];
                setCampusBreakdown(breakdown);
                setActiveCharge(authData.chargeCampus || null);
                storeCampusPaymentCharges(breakdown);
                storeCampusSessions(state.campusSessions || []);
                storePaymentResumeState({
                    ...state,
                    SessionID,
                    parentID,
                    college,
                    campusSessions: state.campusSessions || [],
                    SelectedDiscountValue,
                    DiscountOption: SelectedDiscountValue,
                    SD,
                    FTE,
                    credit_amount,
                    customAmount,
                    enrolledchildrens,
                    childrens,
                    RemAmountMethod,
                    famName,
                    campusPaymentSelections: state.campusPaymentSelections || [],
                    earlyBirdAvailable: state.earlyBirdAvailable,
                    GiveHCCDiscount: state.GiveHCCDiscount,
                });
                setDisplayTotals({
                    TotalResAmount: authData.TotalResAmount,
                    TotalDepFees: authData.TotalDepFees,
                    PayableAmount: authData.PayableAmount,
                    CustomAmount: authData.CustomAmount,
                    RemainingAmount: authData.RemainingAmount,
                    AmountPaidFromAICCredit: authData.AmountPaidFromAICCredit,
                    TotalSchoolFeeswodisc: authData.TotalSchoolFeeswodisc,
                    TotalDiscount: authData.TotalDiscount,
                    TotalFeesWithDiscount: authData.TotalFeesWithDiscount,
                });
                setauthkey(authData.AuthKey);
                if (isDemo) setFormReady(true);

                await URL2.post('/updatefeestructuredata', {
                    SelectedDiscountValue,
                    FTE,
                    feestruc_page_complete: 'Yes',
                    SessionID,
                    campusSessions: state.campusSessions || [],
                    RemAmountMethod,
                });
            } catch (error) {
                // Nothing left to charge is good news, not an error — but only when the server's own
                // breakdown shows every campus paid, never from the message alone. A family who has
                // paid everything and reaches this page again must not be told something went wrong
                // at the moment they finished.
                if (everyCampusPaid(error?.response?.status, error?.response?.data?.calculation)) {
                    clearCampusPaymentCharges();
                    clearPaymentResumeState();
                    setAllPaid(true);
                    return;
                }
                console.error('An error occurred:', error);
                setInitError(error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Unable to initialise BPoint payment. Please try again.');
            } finally {
                setIsInitializing(false);
            }
        };

        init();
    }, [parentID, college, SelectedDiscountValue]);

    useEffect(() => {
        if (!authkey) return;
        // Demo mode has no BPOINT gateway; the simulate button drives the flow instead.
        if (isDemo) return;

        const safeNumber = (value) => {
            const n = Number(value);
            return Number.isFinite(n) ? Math.floor(n) : 0;
        };

        const CRN3Text = `ResF:${safeNumber(TotalResAmount)};REnF:${safeNumber(TotalDepFees)};TotalF:$${safeNumber(PayableAmount)};Paid:$${safeNumber(CustomAmount)}.`;
        const externalScriptURL = process.env.REACT_APP_BPOINT_SCRIPT_URL;
        if (!externalScriptURL) {
            setInitError('BPoint script URL is not configured. Set REACT_APP_BPOINT_SCRIPT_URL in your environment.');
            setFormReady(false);
            return;
        }

        const scriptLoadTimeout = setTimeout(() => {
            setInitError((current) => current || 'The BPoint payment form could not be loaded. Please refresh and try again.');
        }, 20000);

        scriptjs.get(externalScriptURL, () => {
            clearTimeout(scriptLoadTimeout);
            if (typeof window.CBA === 'undefined') {
                setInitError('The BPoint payment form could not be loaded. Please refresh and try again.');
                return;
            }

            window.CBA.SetupPayment({
                AppendToElementId: 'pay-form-location',
                AuthKey: authkey,
                DefaultErrorUrl: `${domainUrl.current}/paymenterror?college=${encodeURIComponent(activeCharge?.college || college)}&session_id=${encodeURIComponent(activeCharge?.SessionID || SessionID)}`,
                Crn1: {
                    Visible: false,
                    LabelName: 'Parent Code',
                    Value: parentID,
                    ReadOnly: true,
                },
                Crn2: {
                    Visible: false,
                    LabelName: 'Purpose',
                    Value: 'Re-enrolment',
                    ReadOnly: true,
                },
                Crn3: {
                    Visible: false,
                    LabelName: 'Fee Breakdown',
                    Value: CRN3Text,
                    ReadOnly: true,
                },
                Amount: {
                    Visible: true,
                    LabelName: 'Amount($)',
                    // This campus's share, not the family's card total.
                    //
                    // It used to be AmountPaidFromBpoint, the whole family's amount, while the
                    // merchant account came from the campus being charged — so a two-campus family
                    // was charged the combined total through the first campus's account. Four
                    // families hit it on 2 and 5 October, leaving $9,310 of Thornlie's money in
                    // Kewdale's bank with no Thornlie transaction to receipt.
                    //
                    // ChargeCampusAmount is what the server says this campus is owed. For a
                    // single-campus family it IS the family total, so nothing changes for them.
                    Value: ChargeCampusAmount,
                    ReadOnly: true,
                },
            });

            const startedAt = Date.now();
            const revealForm = () => {
                try {
                    const payFormElement = document.getElementById('pay-form-location');
                    const apiContainer = document.getElementById('api-container');
                    if (payFormElement && apiContainer) {
                        payFormElement.style.visibility = 'visible';
                        apiContainer.style.maxWidth = '100%';
                        const apiNodes = document.getElementsByClassName('api-node');
                        if (apiNodes.length > 7) {
                            const apinode2 = apiNodes[7];
                            if (apinode2) {
                                const labels = apinode2.getElementsByTagName('label');
                                if (labels.length > 0) labels[0].style.textTransform = 'uppercase';
                            }
                        }
                        setFormReady(true);
                        return;
                    }
                    if (Date.now() - startedAt >= 20000) {
                        setInitError('The BPoint payment form could not be loaded. Please refresh and try again.');
                        return;
                    }
                    styleTimeout.current = setTimeout(revealForm, 200);
                } catch (error) {
                    console.error('Error applying styles:', error);
                    setInitError('The BPoint payment form could not be loaded. Please refresh and try again.');
                }
            };
            styleTimeout.current = setTimeout(revealForm, 200);
        });

        return () => {
            clearTimeout(scriptLoadTimeout);
            if (styleTimeout.current) {
                clearTimeout(styleTimeout.current);
                styleTimeout.current = null;
            }
        };
    }, [authkey, activeCharge, AmountPaidFromBpoint, ChargeCampusAmount, CustomAmount, PayableAmount, TotalDepFees, TotalResAmount, college, parentID, SessionID]);

    if (localStorage.getItem('LoginToken') === null) {
        return null;
    }

    // Demo mode never reaches the BPOINT gateway; jump straight to the receipt.
    const handleSimulateDemoPayment = () => {
        const params = new URLSearchParams({
            SessionId: activeCharge?.SessionID ?? SessionID ?? '',
            ParentID: activeCharge?.parent_code ?? parentID ?? '',
            college: activeCharge?.college ?? college ?? '',
            famName: famName ?? '',
            TotalResAmount: TotalResAmount ?? 0,
            TotalDepFees: TotalDepFees ?? 0,
            TotalSchoolFeeswodisc: state.TotalSchoolFeeswodisc ?? 0,
            TotalDiscount: state.TotalDiscount ?? 0,
            TotalFeesWithDiscount: state.TotalFeesWithDiscount ?? 0,
            PayableAmount: PayableAmount ?? 0,
            CustomAmount: CustomAmount ?? 0,
            RemainingAmount: state.RemainingAmount ?? 0,
            AmountPaidFromAICCredit: state.AmountPaidFromAICCredit ?? 0,
            AmountPaidFromBpoint: AmountPaidFromBpoint ?? 0,
            ResultKey: 'DEMO_RESULT_KEY_998877',
        });
        navigate(`/paymentresponse?${params.toString()}`);
    };

    const content = allPaid ? (
        <Box sx={{ maxWidth: 640, mx: 'auto', my: 4, textAlign: 'center' }} data-testid="everything-paid">
            <Heading text={'Everything is paid'} />
            <Typography sx={{ color: '#334155', fontSize: '15px', mt: 1 }}>
                Every campus has been paid, so there is nothing more to pay. You do not need to enter your card again.
            </Typography>
            <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/reenrolmentcompleted', { replace: true })}
                sx={{ mt: 3, bgcolor: '#059669', fontWeight: 700, textTransform: 'none', borderRadius: '12px', '&:hover': { bgcolor: '#047857' } }}
            >
                See your re-enrolment
            </Button>
        </Box>
    ) : (
        <>
            <Heading text={'Fee Payment'} />
            <ParentDetails
                ParentID={parentID}
                college={college}
                famName={famName}
                childrens={childrens}
            />
            {isMultiStep ? (
                <CampusPaymentSteps
                    steps={steps}
                    currentSessionId={activeCharge?.SessionID}
                    currentAmount={ChargeCampusAmount}
                    started={readyToPay}
                    onStart={() => setStarted(true)}
                />
            ) : (
            <Card
                sx={{
                    mt: 2,
                    mb: 3,
                    border: '1px solid #e2e8f0',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                }}
            >
                <Box sx={{ bgcolor: '#f8fafc', p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '18px' }}>
                        Amount to pay
                    </Typography>
                </Box>
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography sx={{ color: '#0f172a', fontWeight: 800, fontSize: '28px', lineHeight: 1.2 }}>
                        {money(AmountPaidFromBpoint)}
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '13px', mt: 0.5 }}>
                        {cardCampusRows.length > 1
                            ? 'Total card amount for all campuses'
                            : 'Card amount'}
                    </Typography>

                    {cardCampusRows.length > 1 && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px' }}>
                            <Typography sx={{ color: '#1e3a8a', fontSize: '14px', fontWeight: 600 }}>
                                {`Your card will be charged ${cardCampusRows.length} times — once for each campus.`}
                            </Typography>
                            {/*
                              Per campus, and where credit was involved, what it covered as well as
                              what the card pays — otherwise a family whose credit met most of one
                              campus cannot tell why its card amount is smaller than its fees.
                            */}
                            {cardCampusRows.map((row, index) => (
                                <Typography key={row.college} sx={{ color: '#1e40af', fontSize: '13px', mt: 0.5 }}>
                                    {`${index + 1}. ${row.college}: ${money(row.AmountPaidFromBpoint)} on your card`}
                                    {Number(row.credit_applied) > 0
                                        ? ` (${money(row.credit_applied)} of its ${money(row.CustomAmount)} came from credit)`
                                        : ''}
                                </Typography>
                            ))}
                            <Typography sx={{ color: '#1e3a8a', fontSize: '13px', fontWeight: 600, mt: 1 }}>
                                {`Total on your card: ${money(AmountPaidFromBpoint)} across ${cardCampusRows.length} transactions.`}
                            </Typography>
                        </Box>
                    )}

                    {paidCampuses.length > 0 && (
                        <Typography sx={{ color: '#1b5e20', fontSize: '13px', mt: 1.5 }}>
                            {`Already paid: ${paidCampuses
                                .map((row) => `${row.college} ${money(row.alreadyPaidAmount || 0)}`)
                                .join(', ')}.`}
                        </Typography>
                    )}
                    {failedCampuses.length > 0 && (
                        <Typography sx={{ color: '#b00020', fontSize: '13px', fontWeight: 600, mt: 1 }}>
                            {`Payment still needed: ${failedCampuses
                                .map((row) => `${row.college} ${money(row.AmountPaidFromBpoint || 0)}`)
                                .join(', ')}.`}
                        </Typography>
                    )}
                </Box>
            </Card>
            )}

            {initError && (
                <Box sx={{ mb: 3, p: 1.5, bgcolor: '#fef2f2', borderLeft: '4px solid #ef4444', borderRadius: '0 4px 4px 0' }}>
                    <Typography sx={{ color: '#b91c1c', fontSize: '14px', fontWeight: 500 }}>{initError}</Typography>
                </Box>
            )}

            {/* Hidden, not removed, until the parent has seen how many payments there are: BPoint's
                form loads into it either way. Deliberately no "are you sure you want to leave"
                (beforeunload) guard on this page: when a payment goes through, BPoint itself
                navigates the browser away to /paymentresponse, and a guard here would interrupt the
                payment. The guard lives on the screen after a payment (Thankyou.js) instead. */}
            <Box sx={{ position: 'relative', minHeight: formReady || initError ? 0 : 280, display: readyToPay ? 'block' : 'none' }} data-testid="card-form-area">
                {!formReady && !initError && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            bgcolor: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '16px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                            maxWidth: 500,
                            mx: 'auto',
                            width: '100%',
                        }}
                    >
                        <CircularProgress sx={{ color: '#059669' }} />
                        <Typography sx={{ color: '#334155', fontWeight: 600, fontSize: '15px' }}>
                            Loading payment form…
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '13px' }}>
                            Please wait while the secure card form is prepared.
                        </Typography>
                    </Box>
                )}
                <div id="pay-form-location"></div>
            </Box>

            {isDemo && (
                <Box sx={{ mt: 3, p: 3, border: '2px dashed #1976d2', borderRadius: '12px', bgcolor: '#f4f6f9', textAlign: 'center' }}>
                    <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 700 }}>
                        DEMO MODE ACTIVE
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        The external payment gateway is bypassed in demo mode. Use the button below to
                        simulate a successful payment and continue to the receipt.
                    </Typography>
                </Box>
            )}

            <Box
                sx={{
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid #E2E8F0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexDirection: isMobile ? 'column-reverse' : 'row',
                    gap: 1.5,
                }}
            >
                <Button
                    variant="outlined"
                    size="large"
                    startIcon={<NavigateBeforeIcon />}
                    onClick={() => navigate(-1)}
                    sx={{
                        color: '#475569',
                        borderColor: '#cbd5e1',
                        fontWeight: 600,
                        px: 3,
                        py: 1.5,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontSize: '15px',
                        width: isMobile ? '100%' : 'auto',
                    }}
                >
                    Go Back
                </Button>

                {isDemo && (
                    <Button
                        variant="contained"
                        size="large"
                        endIcon={<NavigateNextIcon />}
                        onClick={handleSimulateDemoPayment}
                        sx={{
                            bgcolor: '#059669',
                            color: '#FFFFFF',
                            fontWeight: 700,
                            px: 4,
                            py: 1.5,
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontSize: '15px',
                            boxShadow: '0 4px 14px rgba(5, 150, 105, 0.35)',
                            '&:hover': { bgcolor: '#047857' },
                            width: isMobile ? '100%' : 'auto',
                        }}
                    >
                        Simulate Successful Payment
                    </Button>
                )}
            </Box>
        </>
    );

    return (
        <Box>
            <LoadingBackdrop open={isInitializing} icon={<CircularProgress sx={{ color: '#fff' }} />} />
            <MainTemplate college={college} content={content} SessionID={SessionID} />
        </Box>
    );
};

export default BpointPaymentForm;
